import { useEffect, useRef, useState, type CSSProperties } from "react";
import { Link, useLocation, useParams } from "react-router-dom";
import Footer from "../home/components/Footer";
import Navbar from "../home/components/Navbar";
import NotFound from "../NotFound";
import { getLanguageFromPath } from "@/lib/language";
import { getPortfolioProject, portfolioPageCopy, type PortfolioGalleryItem, withoutEmojis } from "@/lib/portfolio";

function HoverVideo({ src, poster, alt, className, playLabel, style }: { src: string; poster?: string; alt: string; className: string; playLabel: string; style?: CSSProperties }) {
  const videoRef = useRef<HTMLVideoElement>(null);

  const play = () => {
    void videoRef.current?.play().catch(() => undefined);
  };

  const stop = () => {
    const video = videoRef.current;
    if (!video) return;
    video.pause();
    video.currentTime = 0;
  };

  const toggle = () => {
    const video = videoRef.current;
    if (!video) return;
    if (video.paused) play();
    else stop();
  };

  return (
    <figure
      className={className}
      style={style}
      role="button"
      tabIndex={0}
      aria-label={`${alt}. ${playLabel}`}
      onMouseEnter={play}
      onMouseLeave={stop}
      onFocus={play}
      onBlur={stop}
      onClick={toggle}
      onKeyDown={(event) => {
        if (event.key === "Enter" || event.key === " ") {
          event.preventDefault();
          toggle();
        }
      }}
    >
      <video ref={videoRef} src={src} poster={poster} muted loop playsInline preload="metadata" className="block w-full h-auto" aria-label={alt} />
    </figure>
  );
}

const mosaicPlacements = [
  { gridColumn: "1 / 4", gridRow: "1 / 4" },
  { gridColumn: "4 / 6", gridRow: "1 / 4" },
  { gridColumn: "6 / 10", gridRow: "1 / 3" },
  { gridColumn: "10 / 13", gridRow: "1 / 3" },
  { gridColumn: "1 / 3", gridRow: "4 / 6" },
  { gridColumn: "3 / 6", gridRow: "4 / 7" },
  { gridColumn: "10 / 13", gridRow: "3 / 5" },
  { gridColumn: "10 / 13", gridRow: "5 / 7" },
  { gridColumn: "1 / 3", gridRow: "6 / 8" },
  { gridColumn: "6 / 10", gridRow: "3 / 6" },
  { gridColumn: "3 / 6", gridRow: "7 / 9" },
];

const mosaicVideoPlacement = { gridColumn: "6 / 10", gridRow: "6 / 9" };

function setMetaDescription(content: string) {
  let meta = document.querySelector('meta[name="description"]') as HTMLMetaElement | null;
  if (!meta) {
    meta = document.createElement("meta");
    meta.name = "description";
    document.head.appendChild(meta);
  }
  meta.content = content;
}

export default function ProjectPage() {
  const { slug = "" } = useParams();
  const location = useLocation();
  const language = getLanguageFromPath(location.pathname);
  const project = getPortfolioProject(slug);
  const copy = portfolioPageCopy[language];
  const [activeImage, setActiveImage] = useState<{ item: PortfolioGalleryItem; alt: string } | null>(null);

  useEffect(() => {
    if (!project) return;
    const projectCopy = { ...project[language], description: withoutEmojis(project[language].description) };
    document.documentElement.lang = language;
    document.title = `${projectCopy.title} – iVision Design`;
    setMetaDescription(projectCopy.description);
    window.scrollTo({ top: 0, behavior: "auto" });
  }, [language, project]);

  useEffect(() => {
    if (!activeImage) return;

    const closeOnEscape = (event: KeyboardEvent) => {
      if (event.key === "Escape") setActiveImage(null);
    };

    document.body.style.overflow = "hidden";
    window.addEventListener("keydown", closeOnEscape);
    return () => {
      document.body.style.overflow = "";
      window.removeEventListener("keydown", closeOnEscape);
    };
  }, [activeImage]);

  if (!project) {
    return <NotFound />;
  }

  const projectCopy = { ...project[language], description: withoutEmojis(project[language].description) };
  const gallery = project.gallery ?? [{ type: "image" as const, src: project.image }];
  const mobileGallery = project.mobileGallery;
  const isMosaic = project.galleryLayout === "mosaic";
  const galleryImages = gallery.filter((item) => item.type === "image");
  const galleryVideos = gallery.filter((item) => item.type === "video");

  return (
    <div className="min-h-screen font-sans antialiased" style={{ backgroundColor: "#f7f5f0", color: "#003330", fontFamily: "'Garet', sans-serif" }}>
      <div className="fixed top-0 left-0 right-0 h-20 z-40" style={{ backgroundColor: "#004643" }} />
      <Navbar />
      <main className="relative z-10 pt-32 pb-24">
        <div className="max-w-7xl mx-auto px-6 md:px-10">
          <Link
            to={`${language === "en" ? "/en" : "/"}#portfolio?project=${encodeURIComponent(project.slug)}`}
            className="inline-flex items-center gap-2 text-sm font-semibold transition-colors"
            style={{ color: "#004643" }}
          >
            <i className="ri-arrow-left-line" />
            {copy.back}
          </Link>

          <header className="max-w-3xl mx-auto text-center pt-16 pb-14">
            <span className="inline-flex items-center px-4 py-2 rounded-full border text-xs font-semibold tracking-widest uppercase" style={{ borderColor: "rgba(0,70,67,0.2)", color: "#477c72" }}>
              {projectCopy.category}
            </span>
            <h1 className="mt-6 text-5xl md:text-7xl font-bold leading-none" style={{ fontFamily: "'Garet', sans-serif", color: "#003330" }}>
              {projectCopy.title}
            </h1>
            <p className="mt-6 whitespace-pre-line text-base md:text-lg leading-relaxed" style={{ color: "rgba(0,51,48,0.68)" }}>
              {projectCopy.description}
            </p>
          </header>

          <section aria-label={`${projectCopy.title} ${copy.gallery.toLowerCase()}`}>
            {isMosaic ? (
              <div className="portfolio-mosaic">
                {galleryImages.map((item, index) => {
                  const alt = item.alt ?? `${project.alt} ${index + 1}`;
                  const placement = mosaicPlacements[index];
                  if (!placement) return null;

                  return (
                    <button
                      key={item.src}
                      type="button"
                      className="portfolio-mosaic__item"
                      style={placement}
                      aria-label={`${alt} – nagyítás`}
                      onClick={() => setActiveImage({ item, alt })}
                    >
                      <img src={item.src} alt={alt} style={{ objectPosition: item.objectPosition }} />
                    </button>
                  );
                })}
                {galleryVideos.map((item, index) => (
                  <HoverVideo
                    key={item.src}
                    src={item.src}
                    poster={item.poster}
                    alt={item.alt ?? `${project.alt} ${galleryImages.length + index + 1}`}
                    className="portfolio-mosaic__item"
                    style={mosaicVideoPlacement}
                    playLabel={language === "en" ? "Play on focus or touch" : "Lejátszás fókuszálással vagy érintéssel"}
                  />
                ))}
              </div>
            ) : mobileGallery ? (
              <>
                <div className="hidden md:block">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-5 md:gap-7">
                    {gallery.map((item, index) => {
                      const alt = item.alt ?? `${project.alt} ${index + 1}`;
                      const className = `overflow-hidden rounded-[2rem] ${gallery.length === 1 ? "md:col-span-2 max-w-5xl w-full mx-auto" : ""}`;

                      return (
                        <button
                          key={item.src}
                          type="button"
                          className={className}
                          style={{ backgroundColor: "#e9e5dc", border: 0, padding: 0 }}
                          aria-label={`${alt} – nagyítás`}
                          onClick={() => setActiveImage({ item, alt })}
                        >
                          <img src={item.src} alt={alt} className="block w-full h-auto" />
                        </button>
                      );
                    })}
                  </div>
                </div>
                <div className={`nature-mobile-gallery md:hidden${mobileGallery.length === 1 ? " nature-mobile-gallery--single" : ""}`}>
                  {mobileGallery.map((item, index) => {
                    const alt = item.alt ?? `${project.alt} ${index + 1}`;
                    const sizeClass = item.mobileSize === "wide"
                      ? " nature-mobile-gallery__item--wide"
                      : item.mobileSize === "tall"
                        ? " nature-mobile-gallery__item--tall"
                        : "";

                    return (
                      <button
                        key={item.src}
                        type="button"
                        className={`nature-mobile-gallery__item${sizeClass}`}
                        aria-label={`${alt} – nagyítás`}
                        onClick={() => setActiveImage({ item, alt })}
                      >
                        <img src={item.src} alt={alt} style={{ objectPosition: item.objectPosition }} />
                      </button>
                    );
                  })}
                </div>
              </>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-5 md:gap-7">
                {gallery.map((item, index) => {
                  const alt = item.alt ?? `${project.alt} ${index + 1}`;
                  const className = `overflow-hidden rounded-[2rem] ${gallery.length === 1 ? "md:col-span-2 max-w-5xl w-full mx-auto" : ""}`;

                  if (item.type === "video") {
                    return (
                      <HoverVideo
                        key={item.src}
                        src={item.src}
                        poster={item.poster}
                        alt={alt}
                        className={className}
                        playLabel={language === "en" ? "Play on focus or touch" : "Lejátszás fókuszálással vagy érintéssel"}
                      />
                    );
                  }

                  return (
                    <button
                      key={item.src}
                      type="button"
                      className={className}
                      style={{ backgroundColor: "#e9e5dc", border: 0, padding: 0 }}
                      aria-label={`${alt} – nagyítás`}
                      onClick={() => setActiveImage({ item, alt })}
                    >
                      <img src={item.src} alt={alt} className="block w-full h-auto" />
                    </button>
                  );
                })}
              </div>
            )}
          </section>
        </div>
      </main>
      <Footer />

      {activeImage && (
        <div
          className="fixed inset-0 z-[100] flex items-center justify-center p-4 md:p-8"
          style={{ backgroundColor: "rgba(0, 25, 23, 0.94)" }}
          role="dialog"
          aria-modal="true"
          aria-label={activeImage.alt}
          onClick={() => setActiveImage(null)}
        >
          <button
            type="button"
            className="absolute top-5 right-5 z-10 flex h-11 w-11 items-center justify-center rounded-full text-2xl text-white transition-colors hover:bg-white/15"
            aria-label={language === "en" ? "Close image" : "Kép bezárása"}
            onClick={() => setActiveImage(null)}
          >
            <i className="ri-close-line" />
          </button>
          <img
            src={activeImage.item.src}
            alt={activeImage.alt}
            className="max-h-[92vh] max-w-[96vw] object-contain"
            onClick={(event) => event.stopPropagation()}
          />
        </div>
      )}
    </div>
  );
}
